import React from "react";
import {
  AlertTriangle,
  BarChart3,
  Building2,
  Car,
  CalendarDays,
  ClipboardList,
  ChevronDown,
  Eye,
  FileCheck2,
  Plus,
  RefreshCw,
  Search,
  ShieldCheck,
  Users,
  Wrench,
  X,
  Zap,
} from "lucide-react";
import estatesService from "../../services/estatesService";
import TransportWorkspace from "../transport/TransportWorkspace";

const estatesSections = [
  { id: "bookings", label: "Bookings", icon: CalendarDays },
  { id: "spaces", label: "Spaces", icon: Building2 },
  { id: "work_orders", label: "Work orders", icon: Wrench },
  { id: "assets", label: "Assets", icon: ClipboardList },
  { id: "compliance", label: "Compliance", icon: ShieldCheck },
  { id: "utilities", label: "Utilities", icon: Zap },
  { id: "vendors", label: "Vendors & contracts", icon: Users },
  { id: "reports", label: "Reports", icon: BarChart3 },
];

const emptyDashboard = {
  metrics: {},
  spaces: [],
  bookings: [],
  work_orders: [],
  assets: [],
  compliance: [],
  utilities: [],
  vendors: [],
  service_requests: [],
};

const titleCase = (value) =>
  String(value || "—")
    .replaceAll("_", " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());

const dateTimeLabel = (value) =>
  value
    ? new Intl.DateTimeFormat("en-KE", {
        dateStyle: "medium",
        timeStyle: "short",
      }).format(new Date(value))
    : "—";

const dateLabel = (value) =>
  value
    ? new Intl.DateTimeFormat("en-KE", { dateStyle: "medium" }).format(
        new Date(`${String(value).slice(0, 10)}T00:00:00`),
      )
    : "—";

const money = (value) =>
  new Intl.NumberFormat("en-KE", {
    style: "currency",
    currency: "KES",
    maximumFractionDigits: 0,
  }).format(Number(value || 0));

const statusClass = (status) => {
  const value = String(status || "").toUpperCase();
  if (
    ["COMPLETED", "APPROVED", "COMPLIANT", "ACTIVE", "VALID"].includes(value)
  ) {
    return "border-emerald-200 bg-emerald-50 text-emerald-700";
  }
  if (
    ["OVERDUE", "URGENT", "EXPIRED", "HIGH", "REJECTED", "CANCELLED"].includes(
      value,
    )
  ) {
    return "border-rose-200 bg-rose-50 text-rose-700";
  }
  if (["IN_PROGRESS", "ASSIGNED", "PENDING", "QUEUED", "OPEN"].includes(value)) {
    return "border-amber-200 bg-amber-50 text-amber-700";
  }
  return "border-slate-200 bg-slate-50 text-slate-600";
};

const Badge = ({ children, className = "" }) => (
  <span
    className={`inline-flex items-center rounded-full border px-2.5 py-1 text-[11px] font-semibold ${className}`}
  >
    {children}
  </span>
);

const EmptyState = ({ icon: Icon = Building2, title, children }) => (
  <div className="rounded-xl border border-dashed border-slate-300 p-9 text-center">
    <Icon className="mx-auto h-8 w-8 text-slate-300" />
    <p className="mt-2 text-sm font-semibold text-slate-700">{title}</p>
    {children && <p className="mt-1 text-xs text-slate-500">{children}</p>}
  </div>
);

const SectionTitle = ({ icon: Icon, title, description, action }) => (
  <div className="flex flex-wrap items-start justify-between gap-3">
    <div className="flex items-start gap-3">
      {Icon && (
        <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-indigo-50 text-indigo-700">
          <Icon className="h-5 w-5" />
        </span>
      )}
      <div>
        <h2 className="text-lg font-bold text-slate-950">{title}</h2>
        {description && (
          <p className="mt-1 text-sm text-slate-500">{description}</p>
        )}
      </div>
    </div>
    {action}
  </div>
);

const MetricCard = ({ icon: Icon, label, value, detail, tone = "indigo" }) => {
  const tones = {
    indigo: "bg-indigo-50 text-indigo-700",
    emerald: "bg-emerald-50 text-emerald-700",
    amber: "bg-amber-50 text-amber-700",
    rose: "bg-rose-50 text-rose-700",
    sky: "bg-sky-50 text-sky-700",
  };
  return (
    <div className="rounded-lg border bg-white p-3">
      <div className="flex items-start justify-between gap-3">
        <span
          className={`grid h-8 w-8 place-items-center rounded-md ${tones[tone]}`}
        >
          <Icon className="h-3.5 w-3.5" />
        </span>
        <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Live</span>
      </div>
      <p className="mt-3 text-xl font-bold text-slate-950">{value}</p>
      <p className="mt-0.5 text-xs font-semibold text-slate-700">{label}</p>
      {detail && <p className="mt-0.5 text-[11px] text-slate-500">{detail}</p>}
    </div>
  );
};

const normaliseDashboard = (payload) => ({
  ...emptyDashboard,
  ...(payload || {}),
  metrics: payload?.metrics || {},
  spaces: Array.isArray(payload?.spaces) ? payload.spaces : [],
  bookings: Array.isArray(payload?.bookings) ? payload.bookings : [],
  work_orders: Array.isArray(payload?.work_orders) ? payload.work_orders : [],
  assets: Array.isArray(payload?.assets) ? payload.assets : [],
  compliance: Array.isArray(payload?.compliance) ? payload.compliance : [],
  utilities: Array.isArray(payload?.utilities) ? payload.utilities : [],
  vendors: Array.isArray(payload?.vendors) ? payload.vendors : [],
  service_requests: Array.isArray(payload?.service_requests) ? payload.service_requests : [],
});

const EstatesWorkspace = ({
  notify = () => {},
  view = "overview",
  onViewChange,
}) => {
  const [dashboard, setDashboard] = React.useState(emptyDashboard);
  const [loading, setLoading] = React.useState(true);
  const [databaseReady, setDatabaseReady] = React.useState(true);
  const [query, setQuery] = React.useState("");
  const [showWorkOrderForm, setShowWorkOrderForm] = React.useState(false);
  const [showBookingForm, setShowBookingForm] = React.useState(false);
  const [showAmenityOptions, setShowAmenityOptions] = React.useState(false);
  const amenityOptionsRef = React.useRef(null);
  const [showSpaceForm, setShowSpaceForm] = React.useState(false);
  const [showTransportRequest, setShowTransportRequest] = React.useState(false);
  const [serviceCatalogue, setServiceCatalogue] = React.useState([]);
  const [allocationBooking, setAllocationBooking] = React.useState(null);
  const [coordinators, setCoordinators] = React.useState([]);
  const [approvalPreview, setApprovalPreview] = React.useState(null);
  const [allocationForm, setAllocationForm] = React.useState({
    coordinatorId: "",
    notes: "",
  });
  const [busy, setBusy] = React.useState("");
  const [spaceForm, setSpaceForm] = React.useState({
    id: null,
    code: "",
    name: "",
    building: "",
    floor: "",
    spaceType: "classroom",
    capacity: 1,
    status: "ACTIVE",
  });
  const [bookingForm, setBookingForm] = React.useState({
    spaceId: "",
    title: "",
    purpose: "",
    startsAt: "",
    endsAt: "",
    attendees: 1,
    externalRequest: false,
    notes: "",
    meetingNotice: "",
    approvalDocument: null,
    requestedServices: [],
    serviceDetails: "",
  });
  const [workOrderForm, setWorkOrderForm] = React.useState({
    title: "",
    description: "",
    spaceId: "",
    assetId: "",
    trade: "general",
    priority: "normal",
  });

  const viewApprovalDocument = async (booking) => {
    if (!booking.approval_document_path) {
      notify("The approval document is not available for viewing.", "error");
      return;
    }
    setApprovalPreview({
      bookingId: booking.id,
      name: booking.approval_document_name || "Approval document",
      mimeType: booking.approval_document_mime_type || "application/pdf",
      url: "",
      loading: true,
    });
    try {
      const url = await estatesService.getBookingDocumentUrl(
        booking.approval_document_path,
      );
      setApprovalPreview((current) =>
        current?.bookingId === booking.id
          ? { ...current, url, loading: false }
          : current,
      );
    } catch (error) {
      setApprovalPreview(null);
      notify(
        error?.message || "Approval document could not be opened.",
        "error",
      );
    }
  };

  React.useEffect(() => {
    if (!showBookingForm) setShowAmenityOptions(false);
  }, [showBookingForm]);

  React.useEffect(() => {
    if (!showAmenityOptions) return undefined;
    const closeOnOutsideClick = (event) => {
      if (!amenityOptionsRef.current?.contains(event.target)) {
        setShowAmenityOptions(false);
      }
    };
    document.addEventListener("mousedown", closeOnOutsideClick);
    return () => document.removeEventListener("mousedown", closeOnOutsideClick);
  }, [showAmenityOptions]);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const [payload, catalogue] = await Promise.all([
        estatesService.getDashboard(),
        estatesService.getServiceCatalogue(),
      ]);
      setDashboard(normaliseDashboard(payload));
      setServiceCatalogue(catalogue);
      setDatabaseReady(true);
    } catch (error) {
      setDatabaseReady(false);
      notify(
        error?.message ||
          "Estates data could not be loaded. Apply the iFEMS migration first.",
        "error",
      );
    } finally {
      setLoading(false);
    }
  }, [notify]);

  React.useEffect(() => {
    load();
  }, [load]);

  const createWorkOrder = async (event) => {
    event.preventDefault();
    setBusy("work-order");
    try {
      await estatesService.createWorkOrder({
        ...workOrderForm,
        spaceId: workOrderForm.spaceId || null,
        assetId: workOrderForm.assetId || null,
      });
      notify("Work order submitted to Estates.");
      setWorkOrderForm({
        title: "",
        description: "",
        spaceId: "",
        assetId: "",
        trade: "general",
        priority: "normal",
      });
      setShowWorkOrderForm(false);
      await load();
    } catch (error) {
      notify(error?.message || "Work order could not be submitted.", "error");
    } finally {
      setBusy("");
    }
  };

  const createBooking = async (event) => {
    event.preventDefault();
    setBusy("booking");
    try {
      await estatesService.createBooking({
        ...bookingForm,
        startsAt: new Date(bookingForm.startsAt).toISOString(),
        endsAt: new Date(bookingForm.endsAt).toISOString(),
        spaceId: bookingForm.spaceId || null,
        services: bookingForm.requestedServices,
        serviceRequests: bookingForm.requestedServices.map((code) => ({
          code,
          details: bookingForm.serviceDetails,
        })),
      });
      notify("Space booking request submitted for review.");
      setBookingForm({
        spaceId: "",
        title: "",
        purpose: "",
        startsAt: "",
        endsAt: "",
        attendees: 1,
        externalRequest: false,
        notes: "",
        meetingNotice: "",
        approvalDocument: null,
        requestedServices: [],
        serviceDetails: "",
      });
      setShowBookingForm(false);
      await load();
    } catch (error) {
      notify(error?.message || "Booking could not be submitted.", "error");
    } finally {
      setBusy("");
    }
  };

  const openSpaceForm = (space = null) => {
    setSpaceForm(
      space
        ? {
            id: space.id,
            code: space.code || "",
            name: space.name || "",
            building: space.building || "",
            floor: space.floor || "",
            spaceType: space.space_type || "classroom",
            capacity: space.capacity || 1,
            status: space.status || "ACTIVE",
          }
        : {
            id: null,
            code: "",
            name: "",
            building: "",
            floor: "",
            spaceType: "classroom",
            capacity: 1,
            status: "ACTIVE",
          },
    );
    setShowSpaceForm(true);
  };

  const saveSpace = async (event) => {
    event.preventDefault();
    setBusy("space");
    try {
      if (spaceForm.id) {
        await estatesService.updateSpace(spaceForm);
        notify("Space updated.");
      } else {
        await estatesService.createSpace(spaceForm);
        notify("Space added to the Estates inventory.");
      }
      setShowSpaceForm(false);
      await load();
    } catch (error) {
      notify(error?.message || "Space could not be saved.", "error");
    } finally {
      setBusy("");
    }
  };

  const updateBookingStatus = async (booking, status) => {
    setBusy(`booking-${booking.id}`);
    try {
      await estatesService.updateBookingStatus({
        id: booking.id,
        status,
      });
      notify(
        status === "APPROVED"
          ? "Booking approved and space allocated."
          : `Booking ${status.toLowerCase()}.`,
      );
      await load();
    } catch (error) {
      notify(error?.message || "Booking status could not be updated.", "error");
    } finally {
      setBusy("");
    }
  };

  const openAllocation = async (booking) => {
    setBusy(`coordinators-${booking.id}`);
    try {
      const availableCoordinators = await estatesService.getBookingCoordinators(booking.id);
      setCoordinators(availableCoordinators);
      setAllocationBooking(booking);
      setAllocationForm({ coordinatorId: "", notes: "" });
    } catch (error) {
      notify(error?.message || "Coordinators could not be loaded.", "error");
    } finally {
      setBusy("");
    }
  };

  const allocateBooking = async (event) => {
    event.preventDefault();
    if (!allocationBooking || !allocationForm.coordinatorId) return;
    setBusy(`booking-${allocationBooking.id}`);
    try {
      await estatesService.allocateBooking({
        id: allocationBooking.id,
        coordinatorId: allocationForm.coordinatorId,
        notes: allocationForm.notes,
      });
      notify("Booking approved, allocated and assigned to a coordinator.");
      setAllocationBooking(null);
      await load();
    } catch (error) {
      notify(error?.message || "Booking could not be allocated.", "error");
    } finally {
      setBusy("");
    }
  };

  const filteredSpaces = dashboard.spaces.filter((space) => {
    const haystack = [
      space.code,
      space.name,
      space.building,
      space.floor,
      space.space_type,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    return haystack.includes(query.toLowerCase());
  });

  const openView = (nextView) => {
    setQuery("");
    onViewChange?.(nextView);
  };

  const renderOverview = () => (
    <div className="space-y-4">
      <section className="flex flex-wrap items-center justify-between gap-3 rounded-xl border bg-white p-4">
        <div>
          <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-indigo-700">Estates overview</p>
          <h1 className="mt-1 text-xl font-bold text-slate-950">Campus spaces and operations</h1>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setShowWorkOrderForm(true)}
            className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-3 py-2 text-xs font-semibold text-white hover:bg-indigo-700"
          >
            <Plus className="h-3.5 w-3.5" />
            Report issue
          </button>
          <button
            type="button"
            onClick={() => setShowTransportRequest(true)}
            className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50"
          >
            <Car className="h-3.5 w-3.5" />
            Request transport
          </button>
        </div>
      </section>

      {!databaseReady && (
        <div className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" />
          <div>
            <p className="font-semibold">Estates data is not connected yet</p>
            <p className="mt-1">
              Run migration{" "}
              <code>20260918000002_estates_ifems_foundation.sql</code>, then
              refresh this workspace.
            </p>
          </div>
        </div>
      )}

      <section className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
        <MetricCard
          icon={Building2}
          label="Managed spaces"
          value={dashboard.metrics.total_spaces ?? "—"}
          tone="indigo"
        />
        <MetricCard
          icon={CalendarDays}
          label="Active bookings"
          value={dashboard.metrics.active_bookings ?? "—"}
          tone="sky"
        />
        <MetricCard
          icon={Wrench}
          label="Open work orders"
          value={dashboard.metrics.open_work_orders ?? "—"}
          tone="amber"
        />
        <MetricCard
          icon={ShieldCheck}
          label="Overdue compliance"
          value={dashboard.metrics.overdue_compliance ?? "—"}
          tone="rose"
        />
        <MetricCard
          icon={Zap}
          label="Utility spend"
          value={
            dashboard.metrics.utility_spend == null
              ? "—"
              : money(dashboard.metrics.utility_spend)
          }
          detail="Last 90 days"
          tone="emerald"
        />
        <MetricCard
          icon={FileCheck2}
          label="Active contracts"
          value={dashboard.metrics.active_contracts ?? "—"}
          tone="indigo"
        />
      </section>

      <section className="grid gap-4 xl:grid-cols-2">
        <div className="rounded-xl border bg-white p-4">
          <SectionTitle
            icon={CalendarDays}
            title="Upcoming bookings"
            description="Space use that needs visibility from Estates."
            action={
              <button
                type="button"
                onClick={() => openView("bookings")}
                className="text-xs font-semibold text-indigo-700 hover:underline"
              >
                View all
              </button>
            }
          />
          <div className="mt-3 space-y-2">
            {dashboard.bookings.slice(0, 5).map((booking) => (
              <div
                key={booking.id}
                className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-slate-200 px-3 py-2.5"
              >
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-slate-900">
                    {booking.title}
                  </p>
                  <p className="mt-1 text-xs text-slate-500">
                    {booking.space_code || "Space"} ·{" "}
                    {dateTimeLabel(booking.starts_at)}
                  </p>
                </div>
                <Badge className={statusClass(booking.status)}>
                  {titleCase(booking.status)}
                </Badge>
              </div>
            ))}
            {!dashboard.bookings.length && (
              <EmptyState icon={CalendarDays} title="No upcoming bookings">
                Approved space use will appear here.
              </EmptyState>
            )}
          </div>
        </div>
        <div className="rounded-xl border bg-white p-4">
          <SectionTitle
            icon={Wrench}
            title="Maintenance queue"
            description="Reactive work and planned interventions."
            action={
              <button
                type="button"
                onClick={() => openView("work_orders")}
                className="text-xs font-semibold text-indigo-700 hover:underline"
              >
                View all
              </button>
            }
          />
          <div className="mt-3 space-y-2">
            {dashboard.work_orders.slice(0, 5).map((order) => (
              <div
                key={order.id}
                className="rounded-lg border border-slate-200 px-3 py-2.5"
              >
                <div className="flex items-start justify-between gap-3">
                  <p className="text-sm font-semibold text-slate-900">
                    {order.title}
                  </p>
                  <Badge className={statusClass(order.priority)}>
                    {titleCase(order.priority)}
                  </Badge>
                </div>
                <p className="mt-1 text-xs text-slate-500">
                  {order.work_order_number} ·{" "}
                  {order.space_code || "Unassigned space"}
                </p>
              </div>
            ))}
            {!dashboard.work_orders.length && (
              <EmptyState icon={Wrench} title="No open work orders">
                New reports will appear in the queue.
              </EmptyState>
            )}
          </div>
        </div>
      </section>

      {showTransportRequest && (
        <div
          className="fixed inset-0 z-[180] flex items-start justify-center overflow-y-auto bg-slate-950/50 p-3 sm:p-6"
          role="dialog"
          aria-modal="true"
          aria-labelledby="estates-transport-request-title"
        >
          <section className="my-2 w-full max-w-6xl rounded-2xl border border-slate-200 bg-slate-50 p-3 shadow-2xl sm:my-6 sm:p-5">
            <div className="mb-3 flex items-center justify-between gap-3 rounded-xl border bg-white px-4 py-3">
              <div>
                <p className="text-xs font-bold uppercase tracking-wide text-indigo-700">
                  Estates team
                </p>
                <h2
                  id="estates-transport-request-title"
                  className="text-lg font-bold text-slate-950"
                >
                  Request campus transport
                </h2>
              </div>
              <button
                type="button"
                onClick={() => setShowTransportRequest(false)}
                className="grid h-9 w-9 place-items-center rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50"
                aria-label="Close transport request"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <TransportWorkspace
              notify={notify}
              view="requests"
              manager={false}
            />
          </section>
        </div>
      )}
    </div>
  );

  const renderSpaces = () => (
    <div className="space-y-4">
      {showSpaceForm && (
        <div
          className="fixed inset-0 z-[180] flex items-center justify-center bg-slate-950/40 p-4"
          role="presentation"
          onClick={() => setShowSpaceForm(false)}
        >
          <section
            className="max-h-[calc(100vh-2rem)] w-full max-w-3xl overflow-y-auto rounded-2xl bg-white p-5 shadow-2xl sm:p-6"
            role="dialog"
            aria-modal="true"
            aria-labelledby="space-form-title"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-indigo-700">
                  Space inventory
                </p>
                <h2
                  id="space-form-title"
                  className="mt-1 text-xl font-bold text-slate-950"
                >
                  {spaceForm.id ? "Edit space" : "Add space"}
                </h2>
              </div>
              <button
                type="button"
                onClick={() => setShowSpaceForm(false)}
                className="grid h-9 w-9 place-items-center rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50"
                aria-label="Close space form"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <form
              onSubmit={saveSpace}
              className="mt-5 grid gap-3 sm:grid-cols-2"
            >
              <label className="block text-sm font-medium text-slate-700">
                Space code
                <input
                  readOnly
                  value={
                    spaceForm.id ? spaceForm.code : "Generated automatically"
                  }
                  className="mt-1.5 h-10 w-full cursor-not-allowed rounded-lg border border-slate-200 bg-slate-50 px-3 text-sm text-slate-500"
                />
                <p className="mt-1 text-xs font-normal text-slate-500">
                  {spaceForm.id
                    ? "Codes cannot be changed after creation."
                    : "A unique code is assigned when you save."}
                </p>
              </label>
              <label className="block text-sm font-medium text-slate-700">
                Space name
                <input
                  required
                  value={spaceForm.name}
                  onChange={(event) =>
                    setSpaceForm({ ...spaceForm, name: event.target.value })
                  }
                  placeholder="e.g. Senate Boardroom"
                  className="mt-1.5 h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
                />
              </label>
              <label className="block text-sm font-medium text-slate-700">
                Building
                <input
                  required
                  value={spaceForm.building}
                  onChange={(event) =>
                    setSpaceForm({ ...spaceForm, building: event.target.value })
                  }
                  placeholder="e.g. Administration Block"
                  className="mt-1.5 h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
                />
              </label>
              <label className="block text-sm font-medium text-slate-700">
                Floor / level
                <input
                  value={spaceForm.floor}
                  onChange={(event) =>
                    setSpaceForm({ ...spaceForm, floor: event.target.value })
                  }
                  placeholder="e.g. Ground floor"
                  className="mt-1.5 h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
                />
              </label>
              <label className="block text-sm font-medium text-slate-700">
                Space type
                <select
                  value={spaceForm.spaceType}
                  onChange={(event) =>
                    setSpaceForm({
                      ...spaceForm,
                      spaceType: event.target.value,
                    })
                  }
                  className="mt-1.5 h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
                >
                  <option value="classroom">Classroom</option>
                  <option value="boardroom">Boardroom</option>
                  <option value="meeting_room">Meeting room</option>
                  <option value="auditorium">Auditorium</option>
                  <option value="laboratory">Laboratory</option>
                  <option value="office">Office</option>
                  <option value="hall">Hall</option>
                  <option value="outdoor">Outdoor space</option>
                  <option value="other">Other</option>
                </select>
              </label>
              <label className="block text-sm font-medium text-slate-700">
                Maximum capacity
                <input
                  required
                  min="1"
                  type="number"
                  value={spaceForm.capacity}
                  onChange={(event) =>
                    setSpaceForm({ ...spaceForm, capacity: event.target.value })
                  }
                  className="mt-1.5 h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
                />
              </label>
              <label className="block text-sm font-medium text-slate-700">
                Status
                <select
                  value={spaceForm.status}
                  onChange={(event) =>
                    setSpaceForm({ ...spaceForm, status: event.target.value })
                  }
                  className="mt-1.5 h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm"
                >
                  <option value="ACTIVE">Active and bookable</option>
                  <option value="UNDER_MAINTENANCE">Under maintenance</option>
                  <option value="INACTIVE">Inactive</option>
                </select>
              </label>
              <div className="flex justify-end gap-2 border-t border-slate-100 pt-4 sm:col-span-2">
                <button
                  type="button"
                  onClick={() => setShowSpaceForm(false)}
                  className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  disabled={busy === "space"}
                  className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
                >
                  {busy === "space" ? "Saving…" : "Save space"}
                </button>
              </div>
            </form>
          </section>
        </div>
      )}
      <section className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-lg font-bold text-slate-950">Spaces</h2>
            <p className="mt-0.5 text-xs text-slate-500">
              Rooms and bookable campus locations
            </p>
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => openSpaceForm()}
              className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-3 py-2 text-xs font-semibold text-white hover:bg-indigo-700"
            >
              <Plus className="h-4 w-4" />
              Add space
            </button>
            <button
              type="button"
              onClick={load}
              className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50"
            >
              <RefreshCw className="h-4 w-4" />
              Refresh
            </button>
          </div>
        </div>
        <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-slate-100 pt-4">
          <div className="relative w-full max-w-md">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search code, building or room"
              className="h-10 w-full rounded-lg border border-slate-200 pl-9 pr-3 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
            />
          </div>
          <span className="text-xs font-semibold text-slate-500">
            {filteredSpaces.length} spaces
          </span>
        </div>
        <div className="mt-4 overflow-x-auto rounded-lg border border-slate-200">
          <table className="min-w-[960px] w-full text-left text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-3">Code</th>
                <th className="px-4 py-3">Space</th>
                <th className="px-4 py-3">Building / floor</th>
                <th className="px-4 py-3">Type</th>
                <th className="px-4 py-3">Capacity</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {filteredSpaces.map((space) => (
                <tr key={space.id}>
                  <td className="px-4 py-3 font-semibold text-indigo-700">
                    {space.code}
                  </td>
                  <td className="px-4 py-3">
                    <p className="font-semibold text-slate-900">{space.name}</p>
                    <p className="mt-1 text-xs text-slate-500">
                      {space.owner_department_name || "Unassigned department"}
                    </p>
                  </td>
                  <td className="px-4 py-3 text-slate-600">
                    {space.building}
                    {space.floor ? ` · ${space.floor}` : ""}
                  </td>
                  <td className="px-4 py-3 text-slate-600">
                    {titleCase(space.space_type)}
                  </td>
                  <td className="px-4 py-3 font-semibold text-slate-900">
                    {space.capacity}
                  </td>
                  <td className="px-4 py-3">
                    <Badge className={statusClass(space.status)}>
                      {titleCase(space.status)}
                    </Badge>
                  </td>
                  <td className="px-4 py-3">
                    <button
                      type="button"
                      onClick={() => openSpaceForm(space)}
                      className="rounded-lg border border-indigo-200 px-3 py-1.5 text-xs font-semibold text-indigo-700 hover:bg-indigo-50"
                    >
                      Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {!filteredSpaces.length && (
            <div className="p-8">
              <EmptyState icon={Building2} title="No spaces found">
                Add spaces to the Estates inventory to begin allocating rooms.
              </EmptyState>
            </div>
          )}
        </div>
      </section>
    </div>
  );

  const renderBookings = () => (
    <div className="space-y-5">
      <ApprovalDocumentPreview
        preview={approvalPreview}
        onClose={() => setApprovalPreview(null)}
      />
      {allocationBooking && (
        <div
          className="fixed inset-0 z-[80] flex items-center justify-center bg-slate-950/45 p-4 backdrop-blur-[2px]"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setAllocationBooking(null);
          }}
        >
          <section
            role="dialog"
            aria-modal="true"
            aria-labelledby="allocate-booking-title"
            className="max-h-[calc(100vh-2rem)] w-full max-w-3xl overflow-y-auto rounded-2xl border border-emerald-200 bg-white p-5 shadow-2xl sm:p-7"
          >
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-xs font-bold uppercase tracking-wide text-emerald-700">
                  Booking allocation
                </p>
                <h2 id="allocate-booking-title" className="mt-1 text-xl font-bold text-slate-950">
                  Approve and allocate booking
                </h2>
                <p className="mt-3 text-sm font-semibold text-slate-900">
                  {allocationBooking.title || "Space booking"}
                  <span className="ml-2 font-normal text-slate-500">
                    {allocationBooking.booking_number || ""}
                  </span>
                </p>
              </div>
              <button
                type="button"
                aria-label="Close allocation dialog"
                onClick={() => setAllocationBooking(null)}
                className="rounded-lg border border-slate-200 p-2 text-slate-500 hover:bg-slate-50"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <form onSubmit={allocateBooking} className="mt-6 grid gap-4 sm:grid-cols-2">
              <label className="block text-sm font-medium text-slate-700">
                Responsible coordinator
                <select
                  required
                  value={allocationForm.coordinatorId}
                  onChange={(event) => setAllocationForm({ ...allocationForm, coordinatorId: event.target.value })}
                  className="mt-1.5 h-11 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm"
                >
                  <option value="">Select a member of your department</option>
                  {coordinators.map((coordinator) => (
                    <option key={coordinator.id} value={coordinator.id}>
                      {coordinator.full_name} · {titleCase(coordinator.primary_role)}
                    </option>
                  ))}
                </select>
                {!coordinators.length && (
                  <p className="mt-1 text-xs text-rose-600">
                    No department member is available for this booking time.
                  </p>
                )}
              </label>
              <label className="block text-sm font-medium text-slate-700">
                Coordination notes
                <input
                  value={allocationForm.notes}
                  onChange={(event) => setAllocationForm({ ...allocationForm, notes: event.target.value })}
                  placeholder="Setup instructions or handover notes"
                  className="mt-1.5 h-11 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm"
                />
              </label>
              <div className="flex justify-end gap-2 border-t border-slate-100 pt-4 sm:col-span-2">
                <button type="button" onClick={() => setAllocationBooking(null)} className="rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50">Cancel</button>
                <button disabled={busy === `booking-${allocationBooking.id}` || !coordinators.length} className="rounded-lg bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-50">
                  {busy === `booking-${allocationBooking.id}` ? "Allocating…" : "Approve & allocate"}
                </button>
              </div>
            </form>
          </section>
        </div>
      )}
      {showBookingForm && (
        <div
          className="fixed inset-0 z-[75] flex items-center justify-center bg-slate-950/45 p-4 backdrop-blur-[2px]"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setShowBookingForm(false);
          }}
        >
        <section
          role="dialog"
          aria-modal="true"
          aria-labelledby="new-estates-booking-title"
          className="max-h-[calc(100vh-2rem)] w-full max-w-5xl overflow-y-auto rounded-xl border border-slate-200 bg-white p-4 shadow-2xl sm:p-6"
        >
          <div className="flex items-start justify-between gap-3">
            <div>
              <p id="new-estates-booking-title" className="text-base font-bold text-slate-900">
                Request a space
              </p>
            </div>
            <button
              type="button"
              onClick={() => setShowBookingForm(false)}
              className="rounded-lg p-1 text-slate-500 hover:bg-white"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <form
            onSubmit={createBooking}
            className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-4"
          >
            <label className="block text-sm font-medium text-slate-700 sm:col-span-2">
              Space
              <select
                required
                value={bookingForm.spaceId}
                onChange={(event) =>
                  setBookingForm({
                    ...bookingForm,
                    spaceId: event.target.value,
                  })
                }
                className="mt-1.5 h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm"
              >
                <option value="">Select an active space</option>
                {dashboard.spaces
                  .filter((space) => space.status === "ACTIVE")
                  .map((space) => (
                    <option key={space.id} value={space.id}>
                      {space.name} · capacity {space.capacity}
                    </option>
                  ))}
              </select>
            </label>
            <label className="block text-sm font-medium text-slate-700 sm:col-span-2">
              Booking title
              <input
                required
                minLength="3"
                value={bookingForm.title}
                onChange={(event) =>
                  setBookingForm({ ...bookingForm, title: event.target.value })
                }
                placeholder="e.g. Research seminar"
                className="mt-1.5 h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm outline-none focus:border-indigo-500"
              />
            </label>
            <label className="block text-sm font-medium text-slate-700">
              Starts
              <input
                required
                type="datetime-local"
                value={bookingForm.startsAt}
                onChange={(event) =>
                  setBookingForm({
                    ...bookingForm,
                    startsAt: event.target.value,
                  })
                }
                className="mt-1.5 h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm"
              />
            </label>
            <label className="block text-sm font-medium text-slate-700">
              Ends
              <input
                required
                type="datetime-local"
                min={bookingForm.startsAt || undefined}
                value={bookingForm.endsAt}
                onChange={(event) =>
                  setBookingForm({ ...bookingForm, endsAt: event.target.value })
                }
                className="mt-1.5 h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm"
              />
            </label>
            <label className="block text-sm font-medium text-slate-700">
              Attendees
              <input
                required
                min="1"
                type="number"
                value={bookingForm.attendees}
                onChange={(event) =>
                  setBookingForm({
                    ...bookingForm,
                    attendees: event.target.value,
                  })
                }
                className="mt-1.5 h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm"
              />
            </label>
            <label className="flex items-center gap-2 self-end pb-2 text-sm font-medium text-slate-700">
              <input
                type="checkbox"
                checked={bookingForm.externalRequest}
                onChange={(event) =>
                  setBookingForm({
                    ...bookingForm,
                    externalRequest: event.target.checked,
                  })
                }
              />
              External organisation
            </label>
            <fieldset className="rounded-lg border border-slate-200 bg-slate-50 p-3 sm:col-span-2 xl:col-span-4">
              <legend className="px-1 text-sm font-semibold text-slate-800">Additional amenities and services</legend>
              <div ref={amenityOptionsRef} className="relative mt-2">
                <button
                  type="button"
                  aria-expanded={showAmenityOptions}
                  onClick={() => setShowAmenityOptions((value) => !value)}
                  className="flex h-10 w-full items-center justify-between rounded-md border border-slate-300 bg-white px-3 text-left text-sm text-slate-700"
                >
                  <span>
                    {bookingForm.requestedServices.length
                      ? `${bookingForm.requestedServices.length} selected`
                      : "Select amenities and services"}
                  </span>
                  <ChevronDown className={`h-4 w-4 transition-transform ${showAmenityOptions ? "rotate-180" : ""}`} />
                </button>
                {showAmenityOptions && (
                  <div className="absolute left-0 right-0 z-20 mt-1 rounded-md border border-slate-200 bg-white p-2 shadow-xl">
                    <div className="max-h-52 overflow-y-auto">
                      {serviceCatalogue.map((service) => (
                        <label key={service.service_code} className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-2 text-sm text-slate-700 hover:bg-slate-50">
                          <input
                            type="checkbox"
                            checked={bookingForm.requestedServices.includes(service.service_code)}
                            onChange={(event) => setBookingForm({
                              ...bookingForm,
                              requestedServices: event.target.checked
                                ? [...bookingForm.requestedServices, service.service_code]
                                : bookingForm.requestedServices.filter((code) => code !== service.service_code),
                            })}
                          />
                          <span>{service.service_name}</span>
                        </label>
                      ))}
                      {!serviceCatalogue.length && <p className="px-2 py-2 text-sm text-slate-500">No services configured.</p>}
                    </div>
                    <div className="mt-1 flex justify-end border-t border-slate-100 pt-2">
                      <button type="button" onClick={() => setShowAmenityOptions(false)} className="rounded-md px-3 py-1.5 text-xs font-semibold text-indigo-700 hover:bg-indigo-50">
                        Done
                      </button>
                    </div>
                  </div>
                )}
              </div>
              <textarea
                rows="1"
                value={bookingForm.serviceDetails}
                onChange={(event) => setBookingForm({ ...bookingForm, serviceDetails: event.target.value })}
                placeholder="Service details, quantities or setup instructions"
                className="mt-2 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm"
              />
            </fieldset>
            <label className="block text-sm font-medium text-slate-700 sm:col-span-2 xl:col-span-2">
              Purpose / notes
              <textarea
                rows="1"
                value={bookingForm.purpose}
                onChange={(event) =>
                  setBookingForm({
                    ...bookingForm,
                    purpose: event.target.value,
                  })
                }
                placeholder="Purpose, setup requirements or linked services"
                className="mt-1.5 w-full resize-y rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
              />
            </label>
            <label className="block text-sm font-medium text-slate-700 sm:col-span-2 xl:col-span-2">
              Notice to meeting / approval details
              <textarea
                required
                minLength="3"
                rows="1"
                value={bookingForm.meetingNotice}
                onChange={(event) => setBookingForm({ ...bookingForm, meetingNotice: event.target.value })}
                placeholder="Meeting notice, approving office and instructions"
                className="mt-1.5 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
              />
            </label>
            <label className="block text-sm font-medium text-slate-700 sm:col-span-2 xl:col-span-3">
              Approval document
              <input
                required
                type="file"
                accept="application/pdf,image/jpeg,image/png"
                onChange={(event) => setBookingForm({ ...bookingForm, approvalDocument: event.target.files?.[0] || null })}
                className="mt-1.5 block w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
              />
              <span className="mt-1 block text-xs text-slate-500">PDF, JPG or PNG · maximum 5 MB</span>
            </label>
            <div className="flex items-end justify-end gap-2 xl:col-span-1">
              <button
                type="button"
                onClick={() => setShowBookingForm(false)}
                className="rounded-lg border bg-white px-4 py-2 text-sm font-semibold text-slate-700"
              >
                Cancel
              </button>
              <button
                disabled={busy === "booking"}
                className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
              >
                {busy === "booking" ? "Submitting…" : "Submit request"}
              </button>
            </div>
          </form>
        </section>
        </div>
      )}
      <section className="rounded-xl border bg-white p-5 shadow-sm">
        <SectionTitle
          icon={CalendarDays}
          title="Bookings and space allocation"
          description="Review academic, internal and external requests against the space calendar."
          action={
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setShowBookingForm(true)}
                className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-3 py-2 text-xs font-semibold text-white"
              >
                <Plus className="h-4 w-4" />
                New booking
              </button>
              <button
                type="button"
                onClick={load}
                className="inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-xs font-semibold text-slate-700"
              >
                <RefreshCw className="h-4 w-4" />
                Refresh
              </button>
            </div>
          }
        />
        <div className="mt-5 overflow-x-auto rounded-xl border">
          <table className="min-w-[1100px] w-full text-left text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-3">Booking</th>
                <th className="px-4 py-3">Space</th>
                <th className="px-4 py-3">Schedule</th>
                <th className="px-4 py-3">Attendees</th>
                <th className="px-4 py-3">Requester</th>
                <th className="px-4 py-3">Department</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Allocation</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {dashboard.bookings.map((booking) => (
                <tr key={booking.id}>
                  <td className="px-4 py-3">
                    <p className="font-semibold text-slate-900">
                      {booking.title}
                    </p>
                    <p className="mt-1 text-xs text-slate-500">
                      {booking.booking_number}
                    </p>
                    {booking.approval_document_path && (
                      <button
                        type="button"
                        onClick={() => viewApprovalDocument(booking)}
                        className="mt-2 inline-flex items-center gap-1 rounded-md border border-indigo-200 bg-white px-2 py-1 text-xs font-semibold text-indigo-700 hover:bg-indigo-50"
                      >
                        <Eye className="h-3.5 w-3.5" />
                        View approval
                      </button>
                    )}
                  </td>
                  <td className="px-4 py-3 text-slate-600">
                    {booking.space_code} · {booking.space_name}
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-600">
                    {dateTimeLabel(booking.starts_at)}
                    <br />→ {dateTimeLabel(booking.ends_at)}
                  </td>
                  <td className="px-4 py-3 text-slate-600">
                    {booking.attendees || 0}
                  </td>
                  <td className="px-4 py-3 text-slate-600">
                    {booking.requester_name || "—"}
                  </td>
                  <td className="px-4 py-3 text-slate-600">
                    {booking.department_name || "—"}
                  </td>
                  <td className="px-4 py-3">
                    <Badge className={statusClass(booking.status)}>
                      {titleCase(booking.status)}
                    </Badge>
                  </td>
                  <td className="px-4 py-3">
                    {booking.status === "PENDING" && (
                      <div className="flex flex-wrap gap-2">
                        <button
                          type="button"
                          disabled={busy === `booking-${booking.id}`}
                          onClick={() => openAllocation(booking)}
                          className="rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50"
                        >
                          Approve & allocate
                        </button>
                        <button
                          type="button"
                          disabled={busy === `booking-${booking.id}`}
                          onClick={() =>
                            updateBookingStatus(booking, "REJECTED")
                          }
                          className="rounded-lg border border-rose-200 px-3 py-1.5 text-xs font-semibold text-rose-700 disabled:opacity-50"
                        >
                          Reject
                        </button>
                      </div>
                    )}
                    {booking.status === "APPROVED" && (
                      <button
                        type="button"
                        disabled={busy === `booking-${booking.id}`}
                        onClick={() =>
                          updateBookingStatus(booking, "CANCELLED")
                        }
                        className="rounded-lg border border-amber-200 px-3 py-1.5 text-xs font-semibold text-amber-700 disabled:opacity-50"
                      >
                        Release space
                      </button>
                    )}
                    {!["PENDING", "APPROVED"].includes(booking.status) && (
                      <span className="text-xs text-slate-400">No action</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {!dashboard.bookings.length && (
            <div className="p-8">
              <EmptyState icon={CalendarDays} title="No bookings in the queue">
                Space requests will appear here for conflict checking and
                approval.
              </EmptyState>
            </div>
          )}
        </div>
      </section>
      <section className="rounded-xl border bg-white p-5 shadow-sm">
        <SectionTitle
          icon={ClipboardList}
          title="Department service requests"
          description="Approved amenities are queued for their service heads to assign and deliver."
          action={<Badge className={statusClass("QUEUED")}>{dashboard.service_requests.length} queued or assigned</Badge>}
        />
        <div className="mt-4 overflow-x-auto rounded-xl border">
          <table className="min-w-[900px] w-full text-left text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
              <tr><th className="px-4 py-3">Booking</th><th className="px-4 py-3">Service</th><th className="px-4 py-3">Department</th><th className="px-4 py-3">Responsible person</th><th className="px-4 py-3">Status</th></tr>
            </thead>
            <tbody className="divide-y">
              {dashboard.service_requests.map((request) => (
                <tr key={request.id}>
                  <td className="px-4 py-3"><p className="font-semibold text-slate-900">{request.title || request.booking_number}</p><p className="text-xs text-slate-500">{request.space_code} · {dateTimeLabel(request.starts_at)}</p></td>
                  <td className="px-4 py-3 font-medium text-slate-700">{request.service_name}</td>
                  <td className="px-4 py-3 text-slate-600">{titleCase(request.service_department)}</td>
                  <td className="px-4 py-3 text-slate-600">{request.representative_name || "Awaiting department assignment"}</td>
                  <td className="px-4 py-3"><Badge className={statusClass(request.status)}>{titleCase(request.status)}</Badge></td>
                </tr>
              ))}
            </tbody>
          </table>
          {!dashboard.service_requests.length && <p className="p-6 text-center text-sm text-slate-500">No approved amenity requests are waiting for department action.</p>}
        </div>
      </section>
    </div>
  );

  const renderWorkOrders = () => (
    <div className="space-y-5">
      {showWorkOrderForm && (
        <div
          className="fixed inset-0 z-[75] flex items-center justify-center bg-slate-950/45 p-4 backdrop-blur-[2px]"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setShowWorkOrderForm(false);
          }}
        >
        <section
          role="dialog"
          aria-modal="true"
          aria-labelledby="work-order-title"
          className="max-h-[calc(100vh-2rem)] w-full max-w-3xl overflow-y-auto rounded-xl border border-slate-200 bg-white p-5 shadow-2xl sm:p-6"
        >
          <div className="flex items-start justify-between gap-3">
            <div>
              <p id="work-order-title" className="text-base font-bold text-slate-900">
                Report a facility issue
              </p>
            </div>
            <button
              type="button"
              onClick={() => setShowWorkOrderForm(false)}
              aria-label="Close work order form"
              className="rounded-lg border border-slate-200 p-2 text-slate-500 hover:bg-slate-50"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          <form
            onSubmit={createWorkOrder}
            className="mt-4 grid gap-4 sm:grid-cols-2"
          >
            <label className="block text-sm font-medium text-slate-700">
              Issue title
              <input
                required
                minLength="3"
                value={workOrderForm.title}
                onChange={(event) =>
                  setWorkOrderForm({
                    ...workOrderForm,
                    title: event.target.value,
                  })
                }
                placeholder="e.g. Projector not working in 2B-04"
                className="mt-1.5 h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm outline-none focus:border-indigo-500"
              />
            </label>
            <label className="block text-sm font-medium text-slate-700">
              Trade
              <select
                value={workOrderForm.trade}
                onChange={(event) =>
                  setWorkOrderForm({
                    ...workOrderForm,
                    trade: event.target.value,
                  })
                }
                className="mt-1.5 h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm"
              >
                <option value="general">General</option>
                <option value="electrical">Electrical</option>
                <option value="plumbing">Plumbing</option>
                <option value="hvac">HVAC</option>
                <option value="carpentry">Carpentry</option>
                <option value="ict_av">ICT / AV</option>
                <option value="cleaning">Cleaning</option>
              </select>
            </label>
            <label className="block text-sm font-medium text-slate-700">
              Space
              <select
                value={workOrderForm.spaceId}
                onChange={(event) =>
                  setWorkOrderForm({
                    ...workOrderForm,
                    spaceId: event.target.value,
                  })
                }
                className="mt-1.5 h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm"
              >
                <option value="">Select space (optional)</option>
                {dashboard.spaces.map((space) => (
                  <option key={space.id} value={space.id}>
                    {space.code} · {space.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="block text-sm font-medium text-slate-700">
              Priority
              <select
                value={workOrderForm.priority}
                onChange={(event) =>
                  setWorkOrderForm({
                    ...workOrderForm,
                    priority: event.target.value,
                  })
                }
                className="mt-1.5 h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm"
              >
                <option value="normal">Normal</option>
                <option value="low">Low</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </label>
            <label className="block text-sm font-medium text-slate-700 sm:col-span-2">
              Description
              <textarea
                required
                minLength="3"
                rows="3"
                value={workOrderForm.description}
                onChange={(event) =>
                  setWorkOrderForm({
                    ...workOrderForm,
                    description: event.target.value,
                  })
                }
                placeholder="Describe the fault, hazard or maintenance need"
                className="mt-1.5 w-full resize-y rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-indigo-500"
              />
            </label>
            <div className="flex justify-end gap-2 sm:col-span-2">
              <button
                type="button"
                onClick={() => setShowWorkOrderForm(false)}
                className="rounded-lg border bg-white px-4 py-2 text-sm font-semibold text-slate-700"
              >
                Cancel
              </button>
              <button
                disabled={busy === "work-order"}
                className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
              >
                {busy === "work-order" ? "Submitting…" : "Submit work order"}
              </button>
            </div>
          </form>
        </section>
        </div>
      )}
      <section className="rounded-xl border bg-white p-5 shadow-sm">
        <SectionTitle
          icon={Wrench}
          title="Maintenance work orders"
          description="Track reactive issues, preventive work and SLA performance."
          action={
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setShowWorkOrderForm(true)}
                className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-3 py-2 text-xs font-semibold text-white"
              >
                <Plus className="h-4 w-4" />
                New work order
              </button>
              <button
                type="button"
                onClick={load}
                className="rounded-lg border px-3 py-2 text-xs font-semibold text-slate-700"
              >
                <RefreshCw className="h-4 w-4" />
              </button>
            </div>
          }
        />
        <div className="mt-5 overflow-x-auto rounded-xl border">
          <table className="min-w-[900px] w-full text-left text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-3">Work order</th>
                <th className="px-4 py-3">Location</th>
                <th className="px-4 py-3">Trade</th>
                <th className="px-4 py-3">Priority</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Reported</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {dashboard.work_orders.map((order) => (
                <tr key={order.id}>
                  <td className="px-4 py-3">
                    <p className="font-semibold text-slate-900">
                      {order.title}
                    </p>
                    <p className="mt-1 text-xs text-slate-500">
                      {order.work_order_number}
                    </p>
                  </td>
                  <td className="px-4 py-3 text-slate-600">
                    {order.space_code || "—"}
                  </td>
                  <td className="px-4 py-3 text-slate-600">
                    {titleCase(order.trade)}
                  </td>
                  <td className="px-4 py-3">
                    <Badge className={statusClass(order.priority)}>
                      {titleCase(order.priority)}
                    </Badge>
                  </td>
                  <td className="px-4 py-3">
                    <Badge className={statusClass(order.status)}>
                      {titleCase(order.status)}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-500">
                    {dateTimeLabel(order.created_at)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {!dashboard.work_orders.length && (
            <div className="p-8">
              <EmptyState icon={Wrench} title="No work orders">
                Maintenance requests will appear here.
              </EmptyState>
            </div>
          )}
        </div>
      </section>
    </div>
  );

  const renderCollection = (
    sectionId,
    title,
    description,
    Icon,
    rows,
    columns,
    emptyTitle,
  ) => (
    <section className="rounded-xl border bg-white p-5 shadow-sm">
      <SectionTitle
        icon={Icon}
        title={title}
        description={description}
        action={
          <button
            type="button"
            onClick={load}
            className="inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-xs font-semibold text-slate-700"
          >
            <RefreshCw className="h-4 w-4" />
            Refresh
          </button>
        }
      />
      <div className="mt-5 overflow-x-auto rounded-xl border">
        <table className="min-w-[800px] w-full text-left text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
            <tr>
              {columns.map((column) => (
                <th key={column} className="px-4 py-3">
                  {column}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y">
            {rows.map((row) => (
              <tr key={row.id}>
                {sectionId === "assets" && (
                  <>
                    <td className="px-4 py-3 font-semibold text-indigo-700">
                      {row.asset_tag}
                    </td>
                    <td className="px-4 py-3 text-slate-700">{row.name}</td>
                    <td className="px-4 py-3 text-slate-600">
                      {row.space_code || "—"}
                    </td>
                    <td className="px-4 py-3">
                      <Badge className={statusClass(row.condition)}>
                        {titleCase(row.condition)}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-600">
                      {dateLabel(row.next_service_on)}
                    </td>
                  </>
                )}
                {sectionId === "compliance" && (
                  <>
                    <td className="px-4 py-3 font-semibold text-slate-900">
                      {row.requirement}
                    </td>
                    <td className="px-4 py-3 text-slate-600">
                      {row.space_code || row.asset_tag || "—"}
                    </td>
                    <td className="px-4 py-3 text-slate-600">
                      {row.authority || "—"}
                    </td>
                    <td className="px-4 py-3">
                      <Badge className={statusClass(row.status)}>
                        {titleCase(row.status)}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-600">
                      {dateLabel(row.due_on)}
                    </td>
                  </>
                )}
                {sectionId === "utilities" && (
                  <>
                    <td className="px-4 py-3 font-semibold text-slate-900">
                      {row.building}
                    </td>
                    <td className="px-4 py-3 text-slate-600">
                      {titleCase(row.utility_type)}
                    </td>
                    <td className="px-4 py-3 text-slate-600">
                      {row.consumption} {row.unit}
                    </td>
                    <td className="px-4 py-3 font-semibold text-slate-900">
                      {money(row.cost)}
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-600">
                      {dateLabel(row.period_end)}
                    </td>
                  </>
                )}
                {sectionId === "vendors" && (
                  <>
                    <td className="px-4 py-3 font-semibold text-slate-900">
                      {row.name}
                    </td>
                    <td className="px-4 py-3 text-slate-600">
                      {row.contact_name || "—"}
                    </td>
                    <td className="px-4 py-3 text-slate-600">
                      {row.contract_count || 0}
                    </td>
                    <td className="px-4 py-3">
                      <Badge className={statusClass(row.compliance_status)}>
                        {titleCase(row.compliance_status)}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-600">
                      {dateLabel(row.compliance_expires_on)}
                    </td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
        {!rows.length && (
          <div className="p-8">
            <EmptyState icon={Icon} title={emptyTitle}>
              Connect this register to begin tracking Estates records.
            </EmptyState>
          </div>
        )}
      </div>
    </section>
  );

  const renderReports = () => (
    <div className="space-y-5">
      <section className="rounded-xl border bg-white p-5 shadow-sm">
        <SectionTitle
          icon={BarChart3}
          title="Estates reporting"
          description="Executive and operational reporting for space, maintenance, compliance and sustainability."
        />
        <div className="mt-5 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {[
            [
              "Portfolio health",
              "Space count, status and ownership by building",
              Building2,
            ],
            [
              "Maintenance backlog",
              "Reactive versus planned work and SLA performance",
              Wrench,
            ],
            [
              "Compliance register",
              "Expiring inspections and permit-to-work evidence",
              ShieldCheck,
            ],
            ["Sustainability", "Utility spend and consumption trends", Zap],
          ].map(([title, description, Icon]) => (
            <div key={title} className="rounded-xl border border-slate-200 p-4">
              <span className="grid h-9 w-9 place-items-center rounded-lg bg-indigo-50 text-indigo-700">
                <Icon className="h-4 w-4" />
              </span>
              <p className="mt-3 text-sm font-semibold text-slate-900">
                {title}
              </p>
              <p className="mt-1 text-xs leading-5 text-slate-500">
                {description}
              </p>
              <button
                type="button"
                className="mt-3 text-xs font-semibold text-indigo-700 hover:underline"
              >
                Report preview
              </button>
            </div>
          ))}
        </div>
      </section>
    </div>
  );

  if (loading && !dashboard.spaces.length && !dashboard.work_orders.length) {
    return (
      <div className="rounded-xl border bg-white p-10 text-center text-sm text-slate-500">
        Loading Estates workspace…
      </div>
    );
  }

  if (view === "spaces") return renderSpaces();
  if (view === "bookings") return renderBookings();
  if (view === "work_orders") return renderWorkOrders();
  if (view === "assets")
    return renderCollection(
      "assets",
      "Asset register",
      "Track fixed equipment, servicing and space-linked assets.",
      ClipboardList,
      dashboard.assets,
      ["Asset tag", "Asset", "Space", "Condition", "Next service"],
      "No assets recorded",
    );
  if (view === "compliance")
    return renderCollection(
      "compliance",
      "Compliance register",
      "Track inspections, statutory evidence and expiring requirements.",
      ShieldCheck,
      dashboard.compliance,
      ["Requirement", "Location", "Authority", "Status", "Due"],
      "No compliance records",
    );
  if (view === "utilities")
    return renderCollection(
      "utilities",
      "Utilities and sustainability",
      "Monitor electricity, water, gas and other consumption by building.",
      Zap,
      dashboard.utilities,
      ["Building", "Utility", "Consumption", "Cost", "Period end"],
      "No utility readings",
    );
  if (view === "vendors")
    return renderCollection(
      "vendors",
      "Vendors and contracts",
      "Keep contracts, renewal dates and vendor compliance in one register.",
      Users,
      dashboard.vendors,
      ["Vendor", "Contact", "Contracts", "Compliance", "Expires"],
      "No vendors recorded",
    );
  if (view === "reports") return renderReports();
  return renderOverview();
};

const ApprovalDocumentPreview = ({ preview, onClose }) => {
  if (!preview) return null;
  const isImage = String(preview.mimeType || "").startsWith("image/");

  return (
    <div
      className="fixed inset-0 z-[10000] flex items-center justify-center bg-slate-950/60 p-4"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className="flex max-h-[92vh] w-full max-w-5xl flex-col overflow-hidden rounded-xl bg-white shadow-2xl">
        <div className="flex items-center justify-between gap-4 border-b px-5 py-3">
          <div className="min-w-0">
            <p className="text-xs font-semibold uppercase tracking-wide text-indigo-700">
              Approval document
            </p>
            <p className="truncate text-sm font-semibold text-slate-900">
              {preview.name}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close document preview"
            className="rounded-lg border p-2 text-slate-600 hover:bg-slate-50"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="min-h-[55vh] overflow-auto bg-slate-100 p-3">
          {preview.loading ? (
            <div className="grid min-h-[55vh] place-items-center text-sm text-slate-500">
              Loading document…
            </div>
          ) : isImage ? (
            <div className="grid min-h-[55vh] place-items-center">
              <img
                src={preview.url}
                alt={preview.name}
                className="max-h-[72vh] max-w-full rounded-lg object-contain shadow"
              />
            </div>
          ) : (
            <iframe
              title={preview.name}
              src={preview.url}
              className="h-[72vh] min-h-[520px] w-full rounded-lg border bg-white"
            />
          )}
        </div>
        {!preview.loading && preview.url && (
          <div className="flex justify-end gap-2 border-t px-5 py-3">
            <a
              href={preview.url}
              target="_blank"
              rel="noreferrer"
              className="rounded-lg border border-indigo-200 px-3 py-2 text-xs font-semibold text-indigo-700 hover:bg-indigo-50"
            >
              Open in new tab
            </a>
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg bg-indigo-600 px-3 py-2 text-xs font-semibold text-white hover:bg-indigo-700"
            >
              Close
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export { EstatesWorkspace, estatesSections };
export default EstatesWorkspace;
